import {
    Anchor,
    Box,
    Button,
    Card,
    Center,
    Loader,
    LoadingOverlay,
    Stack,
    Text,
    ThemeIcon,
    Title,
    useMantineTheme,
} from '@mantine/core'
import { PaymentElement } from '@stripe/react-stripe-js'
import { PaymentMethodCreateParams } from '@stripe/stripe-js'
import {
    Check,
    ShieldCheck,
    X,
} from 'lucide-react'
import _ from 'lodash'

import { WithViewModelProps } from '@/shared/types/components'
import eventEmitter from '@/utils/event-emitter'
import formatAmount from '@/utils/format-amount'

import { CardFormMachineStates } from './CardForm.machine'
import { useViewModel } from './CardForm.vm'
import StouxIcon from '@/components/atoms/StouxIcon/StouxIcon'
import { useQueryClient } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import { CartContext } from '@/modules/Cart/Cart.vm'

export type CardFormProps = {
    billing_address: PaymentMethodCreateParams.BillingDetails
    client_secret: string
}

const CardForm = (p: CardFormProps) => {
    const viewModel = useViewModel(p)
    return <CardFormView viewModel={viewModel} />
}

const CardFormView = ({
    viewModel,
}: WithViewModelProps<typeof useViewModel>) => {
    const { colors } = useMantineTheme()
    const queryClient = useQueryClient()

    const { setCanCloseCartDrawer } = useContext(CartContext)

    useEffect(() => {
        ;(async () => {
            if (
                viewModel.state.matches('SUCCESS') ||
                viewModel.state.matches('ERROR')
            ) {
                setCanCloseCartDrawer(true)
                await queryClient.invalidateQueries({
                    queryKey: ['PT']
                })
            }

            if (viewModel.state.matches('PROCESSING')) {
                setCanCloseCartDrawer(false)
            }
        })()
    }, [
        queryClient,
        setCanCloseCartDrawer,
        viewModel.state,
        viewModel.state.value,
    ])

    return (
        <>
            {(() => {
                switch (
                    viewModel.state.value as CardFormMachineStates['value']
                ) {
                    case 'FILLING_FORM':
                        return (
                            <>
                                <LoadingOverlay
                                    visible={viewModel.showLoadingOverlay}
                                />
                                <form onSubmit={viewModel.onStartPayment}>
                                    <Stack className="items-end">
                                        <Card
                                            className="text-white flex-col relative pb-6 pt-4 flex w-full"
                                            style={{
                                                background: `linear-gradient(180deg, ${colors.green[9]} 0%, ${colors.green[8]} 100%)`,
                                            }}
                                            shadow="lg"
                                            withBorder
                                            my="sm"
                                        >
                                            <StouxIcon className="absolute top-4 left-4 text-neutral-500" />
                                            <Box className="w-full mt-12">
                                                <PaymentElement
                                                    onChange={
                                                        viewModel.onPaymentElementChange
                                                    }
                                                    options={{
                                                        layout: 'tabs',
                                                        fields: {
                                                            billingDetails: {
                                                                address: {
                                                                    postalCode:
                                                                        'never',
                                                                    country:
                                                                        'never',
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Card>
                                        <Button
                                            type="submit"
                                            color="var(--primary)"
                                            disabled={!viewModel.hasFilledCard}
                                            className="transition-all duration-200 ease-in-out"
                                            leftIcon={
                                                <ShieldCheck className="w-4 h-4" />
                                            }
                                        >
                                            Payer{' '}
                                            {formatAmount(
                                                viewModel.totalAmount,
                                            )}
                                        </Button>
                                    </Stack>
                                </form>
                            </>
                        )
                    case 'PROCESSING':
                        return (
                            <Center mt="xl" className="h-full">
                                <Stack className="items-center">
                                    <Loader variant="dots" color="blue" />
                                    <Text
                                        className="text-xs sm:text-sm"
                                        c="gray.7"
                                    >
                                        Paiement en cours avec Stripe...
                                    </Text>
                                </Stack>
                            </Center>
                        )
                    case 'SUCCESS':
                        return (
                            <Center h="100%" mt="xl">
                                <Stack align="center" className="text-center">
                                    <ThemeIcon
                                        size={80}
                                        variant="light"
                                        color="var(--primary)"
                                        radius={9999}
                                    >
                                        <Check className="w-6 h-6" />
                                    </ThemeIcon>
                                    <Title order={3}>Commande validée !</Title>
                                    <Text size="sm" c="gray.6">
                                        Merci pour votre commande, vous allez
                                        recevoir un email de confirmation dans
                                        quelques instants. <br />
                                        <br />
                                        Vous pouvez également suivre votre
                                        commande depuis votre compte dans
                                        l'onglet "Commandes"
                                    </Text>
                                    <Anchor href="/account?tab=orders">
                                        <Button
                                            color="dark.5"
                                            variant="outline"
                                        >
                                            Accéder à mes commandes
                                        </Button>
                                    </Anchor>
                                </Stack>
                            </Center>
                        )
                    case 'ERROR':
                        return (
                            <Center h="100%" mt="xl">
                                <Stack align="center" className="text-center">
                                    <ThemeIcon
                                        variant="light"
                                        size={80}
                                        color="red"
                                        radius={9999}
                                    >
                                        <X className="w-6 h-6" />
                                    </ThemeIcon>
                                    <Title order={3}>
                                        Oups, un problème est survenue avec le
                                        paiement.
                                    </Title>
                                    <Text size="xs" c="gray.6">
                                        Pas de panique, vous pouvez toujours
                                        revenir à votre panier et réessayer.{' '}
                                        <br />
                                        Si le problème persiste, n'hésitez pas à
                                        nous contacter.
                                    </Text>
                                    <Button
                                        color="dark.5"
                                        onClick={() =>
                                            eventEmitter.emit(
                                                'TOGGLE_CART_DRAWER',
                                            )
                                        }
                                        variant="outline"
                                    >
                                        Fermer le panier
                                    </Button>
                                </Stack>
                            </Center>
                        )
                    default:
                        return null
                }
            })()}
        </>
    )
}

export default CardForm
